import moment from "moment";
// import "moment/locale/ar";
import React, { useState, useRef, useEffect } from "react";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file
import { ar, enGB } from "date-fns/locale";
import { useTranslation } from "react-i18next";

const dateRangeStyles = `
  .theme-date-range {
    background: var(--bg-surface);
    border: 1px solid var(--border-light);
    border-radius: 16px;
    box-shadow: var(--shadow-card);
    overflow: hidden;
  }

  .theme-date-range .rdrCalendarWrapper,
  .theme-date-range .rdrDateDisplayWrapper,
  .theme-date-range .rdrMonthAndYearWrapper,
  .theme-date-range .rdrWeekDays {
    background: var(--bg-surface);
    color: var(--text-main);
  }

  .theme-date-range .rdrMonth,
  .theme-date-range .rdrMonths,
  .theme-date-range .rdrMonthName {
    background: var(--bg-surface);
    color: var(--text-main);
  }

  .theme-date-range .rdrMonthAndYearPickers select,
  .theme-date-range .rdrMonthPicker select,
  .theme-date-range .rdrYearPicker select,
  .theme-date-range .rdrDefinedRangesWrapper,
  .theme-date-range .rdrStaticRange,
  .theme-date-range .rdrInputRanges,
  .theme-date-range .rdrDateInput {
    background: var(--bg-surface-2) !important;
    color: var(--text-main) !important;
    border-color: var(--border-light) !important;
  }

  .theme-date-range .rdrDateInput input,
  .theme-date-range .rdrMonthAndYearPickers select,
  .theme-date-range .rdrMonthPicker select,
  .theme-date-range .rdrYearPicker select {
    color: var(--text-main) !important;
    opacity: 1 !important;
  }

  .theme-date-range .rdrDayNumber span,
  .theme-date-range .rdrWeekDay,
  .theme-date-range .rdrMonthName {
    color: var(--text-main) !important;
    opacity: 1 !important;
  }

  .theme-date-range .rdrDayPassive .rdrDayNumber span {
    color: var(--text-muted) !important;
    opacity: 0.55 !important;
  }

  .theme-date-range .rdrDayDisabled .rdrDayNumber span,
  .theme-date-range .rdrDayPassive.rdrDayDisabled .rdrDayNumber span {
    color: var(--text-muted) !important;
    opacity: 0.35 !important;
  }

  .theme-date-range .rdrDay:not(.rdrDayPassive):not(.rdrDayDisabled)
    .rdrDayNumber
    span,
  .theme-date-range .rdrDayToday:not(.rdrDayPassive) .rdrDayNumber span,
  .theme-date-range .rdrDayStartOfMonth .rdrDayNumber span,
  .theme-date-range .rdrDayEndOfMonth .rdrDayNumber span,
  .theme-date-range .rdrSelected .rdrDayNumber span,
  .theme-date-range .rdrInRange .rdrDayNumber span,
  .theme-date-range .rdrStartEdge .rdrDayNumber span,
  .theme-date-range .rdrEndEdge .rdrDayNumber span {
    color: var(--text-main) !important;
    opacity: 1 !important;
  }

  .theme-date-range .rdrDayToday .rdrDayNumber span:after {
    background: var(--color-primary);
  }

  .theme-date-range .rdrInRange,
  .theme-date-range .rdrStartEdge,
  .theme-date-range .rdrEndEdge {
    color: var(--color-primary);
  }

  .theme-date-range .rdrDayHovered .rdrDayNumber span,
  .theme-date-range .rdrDayStartOfMonth .rdrDayNumber span,
  .theme-date-range .rdrDayEndOfMonth .rdrDayNumber span {
    color: var(--text-main) !important;
  }

  .theme-date-range .rdrDayHovered {
    background: color-mix(in srgb, var(--color-primary) 8%, transparent);
  }

  .theme-date-range .rdrNextPrevButton {
    background: var(--bg-surface-2) !important;
    position: relative;
  }

  .theme-date-range .rdrNextPrevButton i {
    border: none !important;
    width: 10px !important;
    height: 10px !important;
    display: block !important;
    margin: 0 auto !important;
    border-top: 2px solid var(--text-main) !important;
    border-right: 2px solid var(--text-main) !important;
    transform-origin: center !important;
  }

  .theme-date-range .rdrPprevButton i {
    transform: rotate(-135deg) !important;
  }

  .theme-date-range .rdrNextButton i {
    transform: rotate(45deg) !important;
  }

  .theme-date-range .rdrDayStartPreview,
  .theme-date-range .rdrDayInPreview,
  .theme-date-range .rdrDayEndPreview {
    border-color: var(--color-primary) !important;
  }
`;

interface Props {
  label: string;
  onDateRangeChange: (range: any) => void;
  className?: string;
}

function DateRangeDropdown({ label, onDateRangeChange, className = "" }: Props) {
  const [isDateRangeVisible, setDateRangeVisible] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<any>([
    {
      startDate: new Date(new Date().setDate(new Date().getDate() - 1)),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const [cleared, setCleared] = useState(false);

  const { t } = useTranslation();

  const formatDate = (date: any) => {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const getPlaceholder = () => {
    const { startDate, endDate } = state[0];

    if (cleared) {
      return t("reports_page.select_date_range");
    }

    if (!startDate && !endDate) {
      return t("reports_page.select_date_range");
    }

    if (!endDate) {
      return `From ${formatDate(startDate)}`;
    }

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const handleClickOutside = (event: any) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setDateRangeVisible(false);
    }
  };

  const toggleDateRangeVisibility = () => {
    setDateRangeVisible(!isDateRangeVisible);
  };

  const clearDates = () => {
    setCleared(true);
    setState([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
      },
    ]);
  };

  const handleDateRangeChange = (item: any) => {
    setCleared(false);
    setState([item.selection]);

    let { startDate, endDate } = item.selection;

    if (moment(startDate).isSame(moment(endDate), "day")) {
      startDate = moment(startDate).startOf("day").toDate();
      endDate = moment(endDate).endOf("day").toDate();
    }

    onDateRangeChange({ startDate, endDate });
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={`relative grid grid-cols-1 mb-6 rtl:pl-8 ${className}`}
    >
      <style>{dateRangeStyles}</style>
      <label className="mb-1 text-xs text-[var(--text-muted)]">{label}</label>
      <input
        type="text"
        className="h-12 cursor-pointer rounded-lg border border-[var(--border-light)] bg-[var(--bg-surface)] px-4 text-base text-[var(--text-main)] shadow-sm outline-none transition-colors focus:ring-0"
        onClick={toggleDateRangeVisibility}
        value={getPlaceholder()}
        readOnly
      />
      {isDateRangeVisible && (
        <div
          className="theme-date-range absolute top-full z-20 mt-2"
          style={{ minWidth: '340px' }}
        >
          {/* @ts-ignore */}
          <DateRange
            className={
              `theme-date-range ${
                document.documentElement.dir === "rtl"
                  ? "rtl-date-range"
                  : "ltr-date-range"
              }`
            }
            onChange={(item) => {
              setCleared(false);
              setState([item.selection]);
              handleDateRangeChange(item);
            }}
            moveRangeOnFirstSelection={false}
            ranges={state}
            showDateDisplay={false}
            rangeColors={["#00A76F"]}
            color="#FFF"
            locale={document.documentElement.dir === "rtl" ? ar : enGB}
          />
          <div
            className="flex justify-end border-t p-3"
            style={{
              background: "var(--bg-surface-2)",
              borderColor: "var(--border-light)",
            }}
          >
            <button
              className="rounded-md border px-3 py-2 text-sm transition-colors"
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderColor: 'var(--border-light)',
                color: 'var(--text-main)',
              }}
              onClick={clearDates}
            >
              {t("reports_page.clear_dates_btn")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DateRangeDropdown;
